// Cálculo automático dos totais do registro e do Quadro Resumo do S-26.

export const S26_ROW_FIELDS = (n) => ({
  data: `900_${6 + n}_Text_C`,
  desc: `900_${58 + n}_Text`,
  simbolo: `900_${110 + n}_Text_C`,
  donE: `901_${n}_S26Value`,
  donS: `901_${53 + n}_S26Value`,
  contaE: `902_${n}_S26Value`,
  contaS: `902_${53 + n}_S26Value`,
  outraE: `903_${n}_S26Value`,
  outraS: `903_${53 + n}_S26Value`,
});

export const S26_TOTAL_FIELDS = {
  donE: '901_53_S26TotalValue',
  donS: '901_106_S26TotalValue',
  contaE: '902_53_S26TotalValue',
  contaS: '902_106_S26TotalValue',
  outraE: '903_53_S26TotalValue',
  outraS: '903_106_S26TotalValue',
};

export const S26_VALUE_COLS = ['donE', 'donS', 'contaE', 'contaS', 'outraE', 'outraS'];

// Quadro Resumo: saldo anterior (manual), entrada/saída/total (automáticos)
const RESUMO = {
  don: { prev: '904_29_S26Amount', in: '904_30_S26TotalAmount', out: '904_31_S26TotalAmount', total: '904_32_S26TotalAmount', e: 'donE', s: 'donS' },
  conta: { prev: '904_33_S26Amount', in: '904_34_S26TotalAmount', out: '904_35_S26TotalAmount', total: '904_36_S26TotalAmount', e: 'contaE', s: 'contaS' },
  outra: { prev: '904_38_S26Amount', in: '904_39_S26TotalAmount', out: '904_40_S26TotalAmount', total: '904_41_S26TotalAmount', e: 'outraE', s: 'outraS' },
};
const RESUMO_GRAND_TOTAL = '904_42_S26TotalAmount';

export const parseBR = (s) => {
  if (!s) return 0;
  const n = parseFloat(String(s).replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

export const formatBR = (n) =>
  n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Máscara de moeda: dígitos digitados viram centavos (150000 → 1.500,00).
export const maskBRL = (raw) => {
  const digits = String(raw ?? '').replace(/\D/g, '').replace(/^0+(?=\d)/, '');
  if (!digits) return '';
  return formatBR(parseInt(digits, 10) / 100);
};

export const isMoneyField = (name) => /_S26(Total)?(Value|Amount)$/.test(name || '');

export const S26_ROW_KEYS = ['data', 'desc', 'simbolo', 'donE', 'donS', 'contaE', 'contaS', 'outraE', 'outraS'];

// Reordena as linhas do registro em ordem cronológica pelo campo Dia.
// Retorna { campo: valor } com as mudanças, ou null se já estiver ordenado.
export function sortS26Rows(values) {
  const rows = [];
  for (let n = 1; n <= 52; n++) {
    const f = S26_ROW_FIELDS(n);
    const row = {};
    S26_ROW_KEYS.forEach((k) => { row[k] = (values[f[k]] || '').toString(); });
    if (S26_ROW_KEYS.some((k) => row[k].trim())) rows.push(row);
  }
  const dayKey = (r) => {
    const m = r.data.match(/(\d{1,2})(?:\s*[/\-.]\s*(\d{1,2}))?/);
    if (!m) return 9999;
    const d = parseInt(m[1], 10);
    return m[2] ? parseInt(m[2], 10) * 100 + d : d;
  };
  const sorted = rows.map((r, i) => ({ r, i }))
    .sort((a, b) => (dayKey(a.r) - dayKey(b.r)) || (a.i - b.i))
    .map((x) => x.r);
  const empty = Object.fromEntries(S26_ROW_KEYS.map((k) => [k, '']));
  const updates = {};
  for (let n = 1; n <= 52; n++) {
    const f = S26_ROW_FIELDS(n);
    const r = sorted[n - 1] || empty;
    S26_ROW_KEYS.forEach((k) => {
      if ((values[f[k]] || '').toString() !== r[k]) updates[f[k]] = r[k];
    });
  }
  return Object.keys(updates).length ? updates : null;
}

export function computeS26LedgerTotals(values) {
  const t = { donE: 0, donS: 0, contaE: 0, contaS: 0, outraE: 0, outraS: 0 };
  for (let n = 1; n <= 52; n++) {
    const f = S26_ROW_FIELDS(n);
    S26_VALUE_COLS.forEach((c) => { t[c] += parseBR(values[f[c]]); });
  }
  return t;
}

// Avisos por linha quando o símbolo não combina com o lado do valor (D/J = Entrada, G = Saída).
export function computeS26SymbolWarnings(values) {
  const warns = {};
  for (let n = 1; n <= 52; n++) {
    const f = S26_ROW_FIELDS(n);
    const s = (values[f.simbolo] || '').toUpperCase().trim();
    if (!s) continue;
    const entrada = parseBR(values[f.donE]) + parseBR(values[f.contaE]) + parseBR(values[f.outraE]);
    const saida = parseBR(values[f.donS]) + parseBR(values[f.contaS]) + parseBR(values[f.outraS]);
    if ((s === 'D' || s === 'J') && entrada === 0 && saida > 0) {
      warns[n] = `Símbolo ${s} (entrada), mas só há valor de Saída`;
    } else if (s === 'G' && saida === 0 && entrada > 0) {
      warns[n] = 'Símbolo G (gasto), mas só há valor de Entrada';
    }
  }
  return warns;
}

// Retorna { campo: valorFormatado } com totais do registro + Quadro Resumo.
export function computeS26Sync(values) {
  const totals = computeS26LedgerTotals(values);
  const out = {};
  S26_VALUE_COLS.forEach((c) => {
    out[S26_TOTAL_FIELDS[c]] = totals[c] > 0 ? formatBR(totals[c]) : '';
  });
  let grand = 0;
  let grandHasAny = false;
  Object.values(RESUMO).forEach((r) => {
    const entrada = totals[r.e];
    const saida = totals[r.s];
    const prev = parseBR(values[r.prev]);
    const hasAny = Boolean((values[r.prev] || '').toString().trim()) || entrada > 0 || saida > 0;
    out[r.in] = entrada > 0 ? formatBR(entrada) : '';
    out[r.out] = saida > 0 ? formatBR(saida) : '';
    const total = prev + entrada - saida;
    out[r.total] = hasAny ? formatBR(total) : '';
    if (hasAny) { grand += total; grandHasAny = true; }
  });
  out[RESUMO_GRAND_TOTAL] = grandHasAny ? formatBR(grand) : '';
  return out;
}
